/**
 * AES gateway (HLS AES-128 — VOD/LIVE) ops data layer — shared infra.
 *
 * MIGRATION: streams, usage, sessions, and logs now go DIRECTLY to the DRM
 * control plane (controlApi, /api/v1/*, tenant resolved from the JWT) — see
 * streams.ts / usage.ts / sessions.ts / logs.ts. What REMAINS on this @mgmt proxy
 * (serviceApiV3, base …/api/v3, which injects the gateway ADMIN_API_KEY
 * server-side) is only the @aes data-plane runtime:
 *   - GET /services/aes/healthz        → "OK" (text, 200)            [health.ts]
 *   - GET /services/aes/metricsz       → MetricsSnapshot             [metrics.ts]
 * plus the usage-datalake analytics (analytics.ts) and the legacy scrambler
 * whitelist (scrambler.ts) — the latter is SUPERSEDED by the DRM streams console
 * and its @aes routes now answer 410, so that page is legacy pending removal.
 */
import { serviceApiV3 } from '@/api/client';
import { env } from '@/config/env';

/** @mgmt proxy client for the remaining @aes data-plane ops (metricsz/healthz). */
export const aesApi = serviceApiV3;

/**
 * Public gateway host (zone endpoint) players and customer backends call —
 * what /drm/* example URLs on the console render against. Set per-environment
 * via APP_AES_API_URL (a separate service from the control plane); falls back
 * to the legacy test zone only when the env var is unset.
 */
export const AES_GATEWAY_HOST = env.aesGatewayHost || 'https://live.aes.vnetwork.dev';

/** Per-service gateway path: /services/:serviceId/aes/<p> on the control plane. */
export const aesPath = (serviceId: string, p: string) => `/services/${serviceId}/aes${p}`;

// ---- Query keys (scoped by serviceId) ------------------------------------

export const aesKeys = {
  all: (s: string) => ['aes', s] as const,
  health: (s: string) => [...aesKeys.all(s), 'health'] as const,
  metrics: (s: string) => [...aesKeys.all(s), 'metrics'] as const,
  usage: (s: string, params: Record<string, unknown>) => [...aesKeys.all(s), 'usage', params] as const,
  analytics: (s: string, params: Record<string, unknown>) => [...aesKeys.all(s), 'analytics', params] as const,
  logs: (s: string, params: Record<string, unknown>) => [...aesKeys.all(s), 'logs', params] as const,
  sessions: (s: string) => [...aesKeys.all(s), 'sessions'] as const,
  scrambler: (s: string) => [...aesKeys.all(s), 'scrambler'] as const,
};

// ---- Shared event types --------------------------------------------------

/**
 * One structured audit line from the ring buffer. Fields vary by event; the
 * ones below are the common LIVE keyfile shape. The index signature keeps the
 * raw payload accessible (e.g. `key_hex`, `token_fingerprint`, `x_session_id`).
 */
export interface AesLogEvent {
  event: string;
  ts: string;
  ip?: string;
  reason?: string;
  status?: number;
  kind?: string;
  method?: string;
  upstream_url?: string;
  stream?: string;
  [key: string]: unknown;
}

export interface AesLogsResponse {
  events: AesLogEvent[];
  count: number; // returned after filtering
  capacity: number; // applied limit
  total: number; // matched in the window
  window?: string; // look-back window served (e.g. "1h")
}

/** Known event-name prefixes, for the log-viewer filter dropdown. */
export const AES_EVENT_FILTERS: { value: string; label: string }[] = [
  { value: '', label: 'All events' },
  { value: 'http.server_error', label: 'Server error — any 5xx' },
  { value: 'live.keyfile.serve', label: 'Keyfile served (200)' },
  { value: 'live.keyfile.forward', label: 'Keyfile forwarded' },
  { value: 'live.keyfile.canceled', label: 'Client canceled (→499)' },
  { value: 'live.keyfile.upstream_error', label: 'Upstream error (→502)' },
  { value: 'live.keyfile.error', label: 'Internal error (→500)' },
  { value: 'live.keyfile.revoke_degraded', label: 'Revoke degraded (Redis)' },
  { value: 'live.keyfile.reject', label: 'Rejected (auth)' },
  { value: 'live.signed', label: 'Signed URL issue' },
  { value: 'live.upstream', label: 'Upstream API' },
  { value: 'scrambler.serve', label: 'Scrambler key served' },
  { value: 'scrambler.issue', label: 'Scrambler key issued' },
  { value: 'scrambler.reject', label: 'Scrambler rejected' },
];

/**
 * Client-cancellation — the player aborted the key request mid-flight (seek,
 * channel switch, player close, network drop), which cancels the request
 * context. New gateways classify this as `live.keyfile.canceled` (HTTP 499);
 * older pods still log it as `upstream_error`/`error` carrying a "context
 * canceled" reason. Either shape is benign streaming churn, NOT a backend
 * fault, so it must read muted and stay separate from real errors.
 */
export function isClientCanceled(ev: AesLogEvent): boolean {
  if (ev.event === 'live.keyfile.canceled') return true;
  const reason = [ev.reason, ev['detail']]
    .map((v) => (typeof v === 'string' ? v : ''))
    .join(' ')
    .toLowerCase();
  return reason.includes('context canceled') || reason.includes('client closed');
}

/** Which side a client-cancel happened on, for the Detail column. */
export function clientCanceledWhere(ev: AesLogEvent): 'session lookup' | 'upstream fetch' {
  return ev.reason === 'session_lookup' ? 'session lookup' : 'upstream fetch';
}

/** A few event names render as error/warning tones in the viewer. */
export function eventTone(event: string): 'success' | 'danger' | 'warning' | 'muted' {
  if (event.endsWith('.serve')) return 'success';
  if (event.includes('upstream_error') || event.includes('server_error') || event.endsWith('.error'))
    return 'danger';
  // revoke_degraded = Redis revoke check unreachable but key still served
  // (fail-open). Not a hard error — amber, like an auth reject.
  if (event.endsWith('.reject') || event.includes('degraded')) return 'warning';
  return 'muted';
}

/**
 * Tone for a full event row. Same as eventTone, but demotes client-cancellations
 * to muted even when they ride a legacy `upstream_error`/`error` name — so
 * benign player churn never shows red next to real failures.
 */
export function eventToneFor(ev: AesLogEvent): 'success' | 'danger' | 'warning' | 'muted' {
  if (isClientCanceled(ev)) return 'muted';
  return eventTone(ev.event);
}

/** Pull the channel/stream name out of an upstream_url like `…/hls/vtv6plus/key`. */
export function streamOf(ev: AesLogEvent): string | undefined {
  if (typeof ev.stream === 'string' && ev.stream) return ev.stream;
  // Tolerate URLs with or without a trailing path after the stream segment
  // (…/hls/vtv6plus/key?… and …/hls/vtv6plus).
  const m = /\/hls\/([^/?]+)/.exec(ev.upstream_url ?? '');
  return m?.[1];
}
